import type Hls from "hls.js/dist/hls.d.mts";
import type { ManifestLevel } from "../types";
import { getPlaylist } from "@/utils/storage";
import type { HlsConfig } from "hls.js/dist/hls.d.mts";
import { STORAGE_PREVIEW_BASE } from "@/utils/constant";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_QUALITY, buildAuthHeaders, getQualityLabel } from "../utils/player-utils";

const HLS_EXTENSIONS = [".m3u8", "application/vnd.apple.mpegurl"];

const isBareToken = (value?: string) => {
  if (!value) return false;
  if (value.includes("://") || value.startsWith("blob:") || value.startsWith("data:")) {
    return false;
  }
  if (value.startsWith("/") || value.includes("?") || value.includes("#")) {
    return false;
  }
  return /^[A-Za-z0-9_-]{8,}$/.test(value);
};

const isHlsSource = (source?: string) => {
  if (!source) return false;
  const lower = source.toLowerCase();
  if (HLS_EXTENSIONS.some((ext) => lower.includes(ext))) return true;
  if (lower.includes("stream?token")) return true;

  try {
    const url = new URL(source, "http://localhost");
    return url.pathname.toLowerCase().endsWith(".m3u8");
  } catch {
    return false;
  }
};

interface UseHlsControllerParams {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  src?: string;
  payload?: string;
  disableAuthHeaders?: boolean;
  onReady?: () => void;
  onError?: (error: Error) => void;
  onBufferingChange?: (isBuffering: boolean) => void;
}

export const useHlsController = ({
  videoRef,
  src,
  payload,
  disableAuthHeaders,
  onReady,
  onError,
  onBufferingChange,
}: UseHlsControllerParams) => {
  const hlsRef = useRef<Hls | null>(null);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(true);
  const [qualities, setQualities] = useState<string[]>([DEFAULT_QUALITY]);
  const [currentQuality, setCurrentQuality] = useState(DEFAULT_QUALITY);
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const onBufferingChangeRef = useRef(onBufferingChange);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onBufferingChangeRef.current = onBufferingChange;
  }, [onBufferingChange]);

  // VOD-focused config. The previous config was copied from a live-streaming
  // setup (liveSync*, maxLiveSyncPlaybackRate, tiny maxBufferLength, …) which
  // interfered with forward seeking. Keep it minimal: the seek-gap recovery
  // relies on the hole/nudge tolerances below plus snapIntoBufferIfStuck.
  const hlsConfig = useMemo<Partial<HlsConfig>>(
    () => ({
      enableWorker: true,
      lowLatencyMode: false,
      backBufferLength: 30,
      maxBufferLength: 30,
      maxMaxBufferLength: 60,
      maxBufferHole: 0.5,
      maxFragLookUpTolerance: 0.25,
      nudgeOffset: 0.2,
      nudgeMaxRetry: 6,
      startLevel: 0,
      abrEwmaDefaultEstimate: 500_000,
      capLevelToPlayerSize: false,
      capLevelOnFPSDrop: true,
      emeEnabled: false,
    }),
    []
  ); 

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // -----------------------
  // HLS Initialization
  // -----------------------
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let isMounted = true;
    let fragBuffering = false;
    let didNotifyReady = false;
    let metadataHandler: (() => void) | null = null;
    let canPlayHandler: (() => void) | null = null;
    let errorHandler: (() => void) | null = null;
    let onWaiting: (() => void) | null = null;
    let onPlaying: (() => void) | null = null;
    let onSeeked: (() => void) | null = null;

    const notifyReady = () => {
      if (!isMounted || didNotifyReady) return;
      didNotifyReady = true;
      setLoading(false);
      fragBuffering = false;
      onBufferingChangeRef.current?.(false);
      onReadyRef.current?.();
    };

    const initializePlayer = async () => {
      setLoading(true);
      onBufferingChangeRef.current?.(true);
      setQualities([DEFAULT_QUALITY]);
      setCurrentQuality(DEFAULT_QUALITY);

      try {
        const HlsModule = await import("hls.js");
        const Hls = HlsModule.default;
        let playlistUrl = src;

        if (payload && !src) {
          const response = await getPlaylist(payload);
          if (!isMounted) return;
          if (response?.success && response?.data) {
            playlistUrl = `${STORAGE_PREVIEW_BASE}${response.data}`;
          }
        }
        if (!payload && src && isBareToken(src)) {
          const response = await getPlaylist(src);
          if (!isMounted) return;
          if (response?.success && response?.data) {
            playlistUrl = `${STORAGE_PREVIEW_BASE}${response.data}`;
          }
        }

        if (!playlistUrl) throw new Error("No video source or payload provided");

        const headers = disableAuthHeaders ? {} : buildAuthHeaders();
        const shouldUseHls = Hls.isSupported() && isHlsSource(playlistUrl);

        if (shouldUseHls) {
          const hls = new Hls({
            ...hlsConfig,
            xhrSetup: (xhr) => {
              Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
            },
          });

          hlsRef.current = hls;
          hls.loadSource(playlistUrl);
          hls.attachMedia(video);

          const getNetworkQualityPreference = () => {
            if (typeof navigator === "undefined") return "high" as const;
            const connection = (navigator as Navigator & { connection?: { downlink?: number; effectiveType?: string; saveData?: boolean } }).connection;
            const downlink = typeof connection?.downlink === "number" ? connection.downlink : 1;
            const effectiveType = connection?.effectiveType ?? "4g";
            const saveData = Boolean(connection?.saveData);

            if (saveData || effectiveType === "2g") return "low" as const;
            if (effectiveType === "3g") return "medium" as const;
            if (downlink >= 10) return "high" as const;
            if (downlink >= 5) return "medium-high" as const;
            return "medium" as const;
          };

          const pickStartLevel = (levels: ManifestLevel[]) => {
            if (!levels.length) return 0;
            const sorted = [...levels].sort((a, b) => (a.height ?? 0) - (b.height ?? 0));
            const pref = getNetworkQualityPreference();
            const heights = sorted.map((l) => l.height ?? 0);
            const lowest = 0;
            const highest = heights.length - 1;

            if (pref === "low") return lowest;
            if (pref === "high") return highest;
            if (pref === "medium-high") return Math.max(lowest, highest - 1);
            return Math.min(highest, 1);
          };

          // Manifest parsed
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            if (!isMounted) return;
            const levels = hls.levels ?? [];
            setQualities(levels.length ? [DEFAULT_QUALITY, ...levels.map(getQualityLabel)] : [DEFAULT_QUALITY]);
            setCurrentQuality(DEFAULT_QUALITY);
            if (levels.length) {
              const start = pickStartLevel(levels as ManifestLevel[]);
              hls.startLevel = start;
            }
            notifyReady();
          });

          // Level switched
          hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
            const level = hls.levels?.[data.level];
            setCurrentQuality(level ? getQualityLabel(level as ManifestLevel, data.level) : DEFAULT_QUALITY);
          });

          const hasForwardBuffer = (secondsAhead: number) => {
            const media = video;
            if (!media || media.readyState < 2) return false;
            const current = media.currentTime;
            const buffered = media.buffered;
            for (let i = 0; i < buffered.length; i++) {
              const start = buffered.start(i);
              const end = buffered.end(i);
              if (current >= start && current <= end) {
                return end - current >= secondsAhead;
              }
            }
            return false;
          };

          // Recover from the open-GOP seek gap: when we seek forward, the
          // fragment buffers a bit *after* the requested time (first decodable
          // keyframe is later), leaving a hole that stalls playback. Snap the
          // playhead into the freshly buffered range when the gap is small.
          const findForwardRange = (current: number, maxGap: number) => {
            const buffered = video.buffered;
            for (let i = 0; i < buffered.length; i++) {
              const start = buffered.start(i);
              const end = buffered.end(i);
              if (current >= start && current < end) {
                return { start, end, inside: true };
              }
              if (start > current && start - current <= maxGap) {
                return { start, end, inside: false };
              }
            }
            return null;
          };

          const snapIntoBufferIfStuck = () => {
            // NOTE: deliberately do NOT bail on video.seeking. A forward seek
            // into an open-GOP gap never completes — the browser keeps seeking
            // === true forever (no data lands on the exact target, "seeked"
            // never fires), so bailing here meant recovery never ran.
            if (video.ended) return;
            // readyState >= 3 (HAVE_FUTURE_DATA) means the playhead already sits
            // on real data; nothing to recover.
            if (video.readyState >= 3) return;
            // 20s covers a full (~18s) open-GOP segment: after a forward seek the
            // target segment often yields no decodable frames and the buffer only
            // starts at the next keyframe up to a whole segment later.
            const range = findForwardRange(video.currentTime, 40);
            if (range && !range.inside) {
              video.currentTime = range.start + 0.02;
            }
          };

          hls.on(Hls.Events.BUFFER_APPENDED, snapIntoBufferIfStuck);
          hls.on(Hls.Events.FRAG_BUFFERED, snapIntoBufferIfStuck);
          // After the seek settles, the gap may already be buffered.
          onSeeked = snapIntoBufferIfStuck;
          video.addEventListener("seeked", onSeeked);

          // Fragment buffering
          hls.on(Hls.Events.FRAG_LOADING, () => {
            if (loadingRef.current || fragBuffering) return;
            if (hasForwardBuffer(1.0)) return;
            fragBuffering = true;
            onBufferingChangeRef.current?.(true);
          });

          hls.on(Hls.Events.FRAG_LOADED, () => {
            if (fragBuffering && hasForwardBuffer(0.5)) {
              fragBuffering = false;
              onBufferingChangeRef.current?.(false);
            }
          });

          // Also handle native waiting/playing for non-frag stalls
          onWaiting = () => {
            // If the stall is just the open-GOP seek hole, jump over it.
            snapIntoBufferIfStuck();
            if (loadingRef.current || fragBuffering) return;
            if (hasForwardBuffer(0.5)) return;
            fragBuffering = true;
            onBufferingChangeRef.current?.(true);
          };
          onPlaying = () => {
            if (fragBuffering) {
              fragBuffering = false;
              onBufferingChangeRef.current?.(false);
            }
          };
          video.addEventListener("waiting", onWaiting);
          video.addEventListener("playing", onPlaying);
          canPlayHandler = () => {
            notifyReady();
          };
          video.addEventListener("canplay", canPlayHandler);

          // Error
          hls.on(Hls.Events.ERROR, (_, data) => {
            if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
              snapIntoBufferIfStuck();
            }
            if (data.fatal) {
              const error = new Error(`HLS Error: ${data.type}`);
              setLoading(false);
              fragBuffering = false;
              onBufferingChangeRef.current?.(false);
              onErrorRef.current?.(error);
            }
          });
        } else {
          // Fallback to native video
          metadataHandler = () => {
            setQualities([DEFAULT_QUALITY]);
            setCurrentQuality(DEFAULT_QUALITY);
            notifyReady();
          };
          canPlayHandler = () => {
            notifyReady();
          };
          errorHandler = () => {
            const videoError = video.error;
            const error = new Error(
              videoError
                ? `Native video error: ${videoError.code}`
                : "Native video failed to load",
            );
            setLoading(false);
            fragBuffering = false;
            onBufferingChangeRef.current?.(false);
            onErrorRef.current?.(error);
          };
          video.addEventListener("loadedmetadata", metadataHandler);
          video.addEventListener("canplay", canPlayHandler);
          video.addEventListener("error", errorHandler);
          video.src = playlistUrl;
          video.load();
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to initialize player");
        setLoading(false);
        fragBuffering = false;
        onBufferingChangeRef.current?.(false);
        onErrorRef.current?.(error);
      }
    };

    initializePlayer();

    return () => {
      isMounted = false;
      if (metadataHandler) video.removeEventListener("loadedmetadata", metadataHandler);
      if (canPlayHandler) video.removeEventListener("canplay", canPlayHandler);
      if (errorHandler) video.removeEventListener("error", errorHandler);
      if (onWaiting) video.removeEventListener("waiting", onWaiting);
      if (onPlaying) video.removeEventListener("playing", onPlaying);
      if (onSeeked) video.removeEventListener("seeked", onSeeked);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.removeAttribute("src");
      video.load();
    };
  }, [videoRef, src, payload, disableAuthHeaders, hlsConfig]);

  // -----------------------
  // Quality change handler
  // -----------------------
  const handleQualityChange = useCallback(
    (quality: string) => {
      setCurrentQuality(quality);
      const hls = hlsRef.current;
      if (!hls) return;

      if (quality === DEFAULT_QUALITY) {
        hls.currentLevel = -1; // Auto
      } else {
        const levelIndex = qualities.indexOf(quality) - 1;
        if (levelIndex >= 0) hls.currentLevel = levelIndex;
      }
    },
    [qualities]
  );

  return { loading, qualities, currentQuality, handleQualityChange, hlsRef };
};
